import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, Send, MessageCircle } from "lucide-react";

export default function ChatSheet({ rideId, role, peerName, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const quicks =
    role === "driver"
      ? ["q_here", "q_2min", "q_rdv", "q_route"]
      : ["q_coming", "q_waiting", "q_where", "q_thanks"];

  const load = () =>
    api.get(`/rides/${rideId}/messages`).then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [rideId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (val) => {
    const body = (val ?? text).trim();
    if (!body) return;
    setSending(true);
    setText("");
    try {
      const r = await api.post(`/rides/${rideId}/messages`, { text: body });
      setMessages((m) => [...m, r.data]);
    } catch (e) {
      apiError(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] bg-[#1A1A1A]/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
        onClick={onClose}
        data-testid="chat-overlay"
      >
        <motion.div
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="bg-[#FDF8F0] w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl h-[80vh] sm:h-[70vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-modal"
        >
          {/* header */}
          <div className="flex items-center justify-between px-5 h-16 bg-white border-b border-[#E2DFD8]">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#F26419] flex items-center justify-center text-white font-head font-bold">
                {(peerName || "?")[0].toUpperCase()}
              </span>
              <div>
                <div className="font-head font-bold text-[#1A1A1A] leading-tight">{peerName || t("chat.title")}</div>
                <div className="text-xs text-[#75807A]">{role === "driver" ? t("chat.withPax") : t("chat.withDriver")}</div>
              </div>
            </div>
            <button data-testid="chat-close" onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#F0EBE1] flex items-center justify-center">
              <X className="w-5 h-5 text-[#75807A]" />
            </button>
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 no-scrollbar">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center text-[#75807A]">
                <MessageCircle className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm max-w-[220px]">{t("chat.empty")}</p>
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-[#F26419] text-white rounded-br-md" : "bg-white border border-[#E2DFD8] text-[#1A1A1A] rounded-bl-md"}`}>
                    {m.text}
                    <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#75807A]"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* quick replies */}
          <div className="px-3 pt-2 flex gap-2 overflow-x-auto no-scrollbar border-t border-[#E2DFD8] bg-white">
            {quicks.map((q, i) => (
              <button
                key={q}
                data-testid={`chat-quick-${i}`}
                onClick={() => send(t(`chat.${q}`))}
                className="shrink-0 h-9 px-3 rounded-full bg-[#F0EBE1] hover:bg-[#F26419] hover:text-white text-[#3B423E] text-xs font-semibold transition-colors"
              >
                {t(`chat.${q}`)}
              </button>
            ))}
          </div>

          {/* input */}
          <div className="p-3 bg-white flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("chat.placeholder")}
              className="flex-1 h-12 rounded-xl border-2 border-[#E2DFD8] px-4 text-sm focus:outline-none focus:border-[#D94F2B]"
            />
            <button
              data-testid="chat-send"
              onClick={() => send()}
              disabled={sending}
              className="w-12 h-12 rounded-xl bg-[#F26419] hover:bg-[#D85312] flex items-center justify-center text-white disabled:opacity-60"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
